import { FC, useCallback, useMemo } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import { Box, useDisclosure } from '@chakra-ui/react';

import {
  TransactionCategory,
  TransactionPaymentMethod,
} from '__generated__/GQL';
import flag from 'assets/brand/red-flag.png';
import useCompanyContext from 'common/hooks/useCompanyContext';
import Button from 'components/_core/Button';
import Callout from 'components/_core/Callout';
import Modal from 'components/_core/Modal';
import SunshineCard from 'components/_core/SunshineCard';
import Text from 'components/_core/Text';
import BackButton from 'components/BackButton';
import ErrorView from 'components/ErrorView';
import Loader from 'components/Loader';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import { useBankOverviewSearchParams } from 'features/Bank/pages/Overview/hooks/useBankOverviewSearchParams';
import {
  getDisputeSuccessPath,
  getDisputeTransactionsPath,
} from 'features/Bank/routes';
import * as ContentLayout from 'layouts/ContentLayout';

import DisputableTransactionRow from '../../components/DisputableTransactionRow';
import StickyFooter from '../../components/StickyFooter';
import { MAX_TRANSACTIONS_PER_DISPUTE } from '../../constants';
import { useDisputableTransactions } from '../../hooks/useDisputableTransactions';
import { useToggleSelectedTransactions } from '../../hooks/useToggleSelectedTransactions';
import { CreateDisputeDocument } from './graphql/createDispute.gql';
import locales from './locales';

const DisputeForm: FC = () => {
  const { search } = useLocation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { getRootPath, getTransactionHelpPath } = useBankOverviewSearchParams();
  const transactionIds = useMemo(
    () => new URLSearchParams(search).getAll('transactionId'),
    [search],
  );
  const firstTransactionId = transactionIds?.[0];

  const { companyUserId } = useCompanyContext();
  const history = useHistory();
  const { showToast } = useToast();

  const [createDispute, { loading: isLoadingCreate }] = useMutation(
    CreateDisputeDocument,
  );

  const { selectedTransactionIds, toggle } =
    useToggleSelectedTransactions(transactionIds);

  const { disputableTransactions, error, isLoading } =
    useDisputableTransactions(transactionIds, MAX_TRANSACTIONS_PER_DISPUTE);

  const submit = useCallback(async () => {
    logEvent({ name: 'Submit_Dispute_Transaction' });
    const bankAccountIds = disputableTransactions
      .filter((transaction) =>
        selectedTransactionIds.includes(transaction.transactionId),
      )
      .map((transaction) => transaction.bankAccountId);

    await createDispute({
      onCompleted: (data) => {
        history.push(
          getDisputeSuccessPath(data.createDispute.intercomConversationId),
        );
      },
      onError: () => {
        showToast({
          message: locales.error,
          type: 'error',
        });
      },
      variables: {
        input: {
          bankAccountIds,
          companyUserId: companyUserId ?? '',
          transactionIds: selectedTransactionIds,
        },
      },
    });
  }, [
    disputableTransactions,
    createDispute,
    companyUserId,
    selectedTransactionIds,
    history,
    showToast,
  ]);

  const hasSecureTransactions = useMemo(
    () =>
      disputableTransactions.some(
        (transaction) =>
          transaction.is3DS ||
          transaction.paymentMethod !== TransactionPaymentMethod.Card ||
          transaction.category === TransactionCategory.Cash,
      ),
    [disputableTransactions],
  );

  if (error) {
    return <ErrorView />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <ContentLayout.Root>
      <ContentLayout.Body paddingBottom="0" size="sm">
        <BackButton
          marginBottom={32}
          to={
            firstTransactionId
              ? getTransactionHelpPath(firstTransactionId)
              : getRootPath()
          }
        />
        <ContentLayout.Header title={locales.title} />

        {hasSecureTransactions ? (
          <Callout hideIcon mb="space-32">
            <Text>{locales.warning}</Text>
          </Callout>
        ) : null}

        <Text fontSize="medium" fontWeight="weight-500" variant="primary">
          {locales.formatString(
            selectedTransactionIds.length > 1
              ? locales.transactionsCount
              : locales.transactionCount,
            {
              count: selectedTransactionIds.length,
            },
          )}
        </Text>
        <SunshineCard.Group mt="space-12">
          {disputableTransactions?.map((transaction) => (
            <DisputableTransactionRow
              isSelected={selectedTransactionIds.includes(
                transaction.transactionId,
              )}
              key={transaction.transactionId}
              onChange={toggle}
              transaction={transaction}
            />
          ))}
        </SunshineCard.Group>

        <Box mt="space-16">
          <Button
            as={Link}
            icon="plus"
            iconPosition="left"
            onClick={() =>
              logEvent({ name: 'Click_Dispute_Transaction_Multiple' })
            }
            to={getDisputeTransactionsPath(selectedTransactionIds)}
            variant="inline-secondary"
          >
            {locales.addTransaction}
          </Button>
        </Box>

        <StickyFooter>
          <Button
            icon="arrow-right"
            isDisabled={selectedTransactionIds.length < 1}
            onClick={onOpen}
          >
            {locales.send}
          </Button>
        </StickyFooter>
      </ContentLayout.Body>
      <Modal isOpen={isOpen} onClose={onClose}>
        <Modal.Header />
        <Modal.Image boxSize="140px" mt="space-32" src={flag} />
        <Modal.Title px="space-18">{locales.important}</Modal.Title>
        <Modal.Description mb="space-32" px="space-18">
          {locales.legalMention}
        </Modal.Description>
        <Modal.Footer justifyContent="flex-end">
          <Button isLoading={isLoadingCreate} onClick={submit}>
            {locales.confirm}
          </Button>
        </Modal.Footer>
      </Modal>
    </ContentLayout.Root>
  );
};

export default DisputeForm;
